import { useMemo } from "react";
import { useLocation } from "react-router";
import { useIntl, defineMessages } from "react-intl";
import { Wrapper } from "../components/layout/wrapper";
import { menu_translations } from "../i18n/sharable-defs";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import RatioImage from "../components/medias/ratio-image";
import PostCard from "../components/post-card";
import { useAppConfig } from "@ct-react/core";
import { useQuery } from "@apollo/client/react";
import { CmsPageContent, extractCmsContent } from "@shared/graphql";
import EditorialSeo from "../components/layout/editorial-seo";
import { gql } from "@apollo/client";
import { getPageDataByPath } from "@shared/cms";
import { NewsEntry, useNewsContext } from "../contexts/news";
import Skeleton from "react-loading-skeleton";
import { buildPageUrl } from "@shared/url";
import NotFoundPage from "./not-found";

import "./news.scss";

const CMS_PAGE_GQL_DATA = gql`
  query NewsPageCmsPage($templateId: UUID!) {
    cmsPages(templateId: $templateId) { data }
  }
`;

const transDefs = defineMessages({
  features_new_title: { id: "news.feature-new-title", defaultMessage: "Notre dernier article à la une" },
  new_title: { id: "news.new-title", defaultMessage: "Tous nos articles" },
  new_btn: { id: "news.btn", defaultMessage: "Lire l'article" },
});

const News = () => {
  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { pathname } = useLocation();
  const { loading: newsLoading, newsContent } = useNewsContext();
  const { options: { cmsTemplates: { editorial: templateId } } } = useAppConfig();

  const { data, loading: cmsLoading } = useQuery<{ cmsPages: any }>(CMS_PAGE_GQL_DATA, {
    ssr: true,
    variables: { templateId }
  });

  const cmsContent = useMemo<CmsPageContent | undefined>(() => {
    if (!data?.cmsPages?.length) return undefined;
    const page = getPageDataByPath(data.cmsPages, pathname);
    return page ? extractCmsContent(page, locale) : undefined;
  }, [ data, pathname, locale ]);

  if (!cmsLoading && Array.isArray(data?.cmsPages) && !cmsContent) return <NotFoundPage />;

  const seo = cmsContent?.["seo-data"];

  return(
    <>

      <EditorialSeo page="news" seo={seo} />

      <Wrapper.Centered className="banner">
        <h1>{intl.formatMessage(menu_translations.news)}</h1>
        <hr></hr>
      </Wrapper.Centered>

      <Wrapper.TwoCol as="section"
                      className="reverse featured-new"
                      title={intl.formatMessage(transDefs.features_new_title)}>
        <Wrapper.ColText>
          {newsLoading && <>
            <h3><Skeleton/></h3>
            <p><Skeleton/></p>
            <Skeleton width="110px" height="44px"/>
          </>}
          {(!newsLoading && newsContent && newsContent.length > 0) && <>
            <h3>{newsContent[0].data["news-title"]}</h3>
            {!!newsContent[0].data["news-lead"] && <p>{newsContent[0].data["news-lead"]}</p>}
            <LocaleLink to={`/actualites/${buildPageUrl(newsContent[0].data["news-title"])}?refId=${newsContent[0].id}`}
                        className="btn primary">{intl.formatMessage(transDefs.new_btn)}</LocaleLink>
          </>}
        </Wrapper.ColText>
        <Wrapper.ColImage>
          {newsLoading && <picture className="rimg skeleton"><Skeleton height="100%"/></picture>}
          {(!newsLoading && newsContent && newsContent.length > 0) &&
            <RatioImage source={newsContent[0].data["news-image"].assets}
                        alt={newsContent[0].data["news-image"].alternativeText || newsContent[0].data["news-title"]}/>}
        </Wrapper.ColImage>
      </Wrapper.TwoCol>

      <Wrapper.Centered as="div">
        <hr className="hr"></hr>
      </Wrapper.Centered>

      <Wrapper.Centered as="section"
                        className="news">
        <h2>{intl.formatMessage(transDefs.new_title)}</h2>
        <div className="news-container">
          {newsLoading && Array.from({length: 4}).map((_, index) => <PostCard key={index} data={null} />)}
          {(!newsLoading && newsContent) && newsContent.map((post: NewsEntry, index: number) => <PostCard key={index} data={post} />)}
        </div>
      </Wrapper.Centered>

    </>
  )
};

export default News;
